import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import type { TrailEntry } from './progressTrail';

/**
 * English fallbacks for the projected activity keys (spec §6). These are the
 * `defaultValue`s passed to `t`; the `chat.activity.*` locale entries win in
 * every real language, and an unrecognized key degrades to `working` so a new
 * backend activity can never render a raw identifier.
 */
const ACTIVITY_LABELS: Record<string, string> = {
  working: 'Working…',
  market_data: 'Gathering market data',
  research_flow: 'Researching',
  web_search: 'Searching the web',
  documents: 'Reviewing documents',
  calculation: 'Calculating',
};

interface ChatProgressPanelProps {
  /** Completed tool calls, consecutive repeats collapsed. Newest last. */
  trail: TrailEntry[];
  /** Every completed call this turn, including ones the capped trail dropped. */
  total: number;
  /** Epoch ms when the turn's stream opened — drives the elapsed timer. */
  startedAt: number;
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Milliseconds below a second, so the 1–540ms calls that dominate a real turn
 * stay legible instead of all rounding to "0.0s".
 */
function formatDuration(ms: number): string {
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Live work-in-progress indicator shown while a turn is streaming but has not
 * produced user-visible text yet.
 *
 * A research turn produces no streamed text at all until the agent emits its
 * envelope at the very end of the run, so without this the user sees only
 * "Thinking…" for the whole live window and has no way to tell a 4-minute
 * research run from a hung request.
 *
 * It shows *accumulated* work rather than current work, because the two are not
 * interchangeable here. Tool calls last 1–540ms and are separated by long
 * stretches of LLM inference that publish nothing, so an indicator bound to the
 * in-flight tool is on screen under 1% of the time and reads as a frozen
 * spinner. A trail of completed calls plus a running total moves on roughly
 * every few seconds, which is the actual liveness signal — and the per-call
 * durations show the user directly that the tools are not what is slow.
 *
 * Unmounts when the stream closes, at which point the persisted "🔬 仍在研究中"
 * ack bubble takes over as the long-running indicator.
 *
 * Only structured signals are rendered: each trail row's `tool` is a coarse
 * activity key projected by the backend (never a plugin identifier), shown
 * through a localized label; counts and durations are numbers. No
 * model-generated prose reaches this component.
 */
export function ChatProgressPanel({ trail, total, startedAt }: ChatProgressPanelProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <View style={styles.panel}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
          <Text style={styles.header}>{t('chat.working', { defaultValue: 'Working…' })}</Text>
        </View>
        <Text style={styles.elapsed}>{formatElapsed(now - startedAt)}</Text>
      </View>

      <View style={styles.separator} />

      {trail.length === 0 ? (
        <Text style={styles.detail}>{t('chat.thinking', { defaultValue: 'Thinking...' })}</Text>
      ) : (
        trail.map((entry, i) => (
          <View key={`${i}-${entry.tool}`} style={styles.trailRow}>
            <View style={styles.trailLabel}>
              <Text style={styles.trailTool} numberOfLines={1}>
                {t(`chat.activity.${entry.tool}`, {
                  defaultValue: ACTIVITY_LABELS[entry.tool] ?? ACTIVITY_LABELS.working,
                })}
              </Text>
              {entry.count > 1 && <Text style={styles.trailCount}>{`×${entry.count}`}</Text>}
            </View>
            {entry.durationMs !== undefined && (
              <Text style={styles.trailDuration}>{formatDuration(entry.durationMs)}</Text>
            )}
          </View>
        ))
      )}

      {total > 0 && (
        <>
          <View style={styles.separator} />
          <Text style={styles.total}>
            {t('chat.stepTotal', { defaultValue: `Step ${total}`, count: total })}
          </Text>
        </>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    panel: {
      // Full width: this is a ten-row status list now, not a one-line badge, and
      // the rows need the room. flexShrink/minWidth/overflow are the
      // portrait-overflow guard and must survive the widening.
      alignSelf: 'stretch',
      flexShrink: 1,
      minWidth: 0,
      overflow: 'hidden',
      gap: theme.spacing.xxs,
      marginVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      borderRadius: theme.radii.lg,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.surfaceSecondary,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.sm,
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flexShrink: 1,
      minWidth: 0,
      gap: theme.spacing.sm,
    },
    header: {
      flexShrink: 1,
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    elapsed: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      // Fixed-width digits so the ticking timer does not jitter the row.
      fontVariant: ['tabular-nums'],
    },
    separator: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: theme.colors.border,
    },
    detail: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
    },
    trailRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    trailLabel: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
      gap: theme.spacing.xxs,
    },
    trailTool: {
      flexShrink: 1,
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeXs,
    },
    trailCount: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
    },
    trailDuration: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      // Fixed-width digits so a changing duration does not jitter the column.
      fontVariant: ['tabular-nums'],
    },
    total: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
  });
}
